import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CreditCard, CheckCircle2, Sparkles, Landmark, Pencil,
  ArrowUpRight, FileText, TrendingUp,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Dashboard";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  Stepper,
} from "@/pages/formation/FormationSteps";
import {
  ProfileStep, DunsStep, BureausStep, VendorsStep, TimelineStep,
  CreditDocumentsStep, CreditReviewStep,
} from "@/pages/credit/CreditSteps";

function CompletedView({ wf, plan, onEdit }) {
  const navigate = useNavigate();
  return (
    <div className="fv-fade-up max-w-4xl" data-testid="credit-completed">
      <PageHeader eyebrow="Build" title="Business Credit Center"
        subtitle="Your business credit profile is set up. Everything is synced to your Business Profile and shared across Fairview Systems." />
      <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-6 mb-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><TrendingUp className="w-6 h-6 text-[#0A0A0B]" /></div>
        <div className="flex-1">
          <div className="font-heading text-2xl text-white">Funding readiness: {(plan || wf.funding_readiness).pct || wf.funding_readiness.pct}%</div>
          <div className="text-sm text-[#A1A1AA]">Business credit score {wf.credit_score.score} ({wf.credit_score.tier}) · Credibility {wf.credibility.score}%</div>
        </div>
        <button onClick={onEdit} data-testid="credit-edit-btn" className="flex items-center gap-1.5 text-sm h-10 px-4 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors shrink-0"><Pencil className="w-4 h-4" /> Edit</button>
      </div>

      {plan && (
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-6" data-testid="funding-plan">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="w-5 h-5 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Your Funding Preparation Plan</h3></div>
          <div className="space-y-3">
            {plan.steps.map((s, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`plan-step-${i}`}>
                <span className="w-6 h-6 rounded-full fv-gold-gradient text-[#0A0A0B] text-xs flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <div><div className="text-sm text-white">{s.title}</div><div className="text-xs text-[#71717A]">{s.detail}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <button onClick={() => navigate("/funding")} data-testid="credit-next-funding" className="text-left rounded-xl border border-[#D4AF37]/30 bg-[#151310] p-4 hover:border-[#D4AF37]/50 transition-colors flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><Landmark className="w-4 h-4 text-[#D4AF37]" /></div>
          <div><div className="text-white text-sm font-medium">Open the Funding Center</div><div className="text-xs text-[#71717A]">Match loans, grants & investors to your profile.</div></div>
        </button>
        <button onClick={() => navigate("/documents")} data-testid="credit-next-documents" className="text-left rounded-xl border border-white/8 bg-[#121214] p-4 hover:border-[#D4AF37]/40 transition-colors flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-[#D4AF37]" /></div>
          <div><div className="text-white text-sm font-medium">View your credit documents</div><div className="text-xs text-[#71717A]">5 reports saved to your Document Library.</div></div>
        </button>
      </div>

      <button onClick={() => navigate("/ai-assistant")} data-testid="credit-completed-ai-cta"
        className="w-full rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#151310] to-[#121214] p-5 flex items-center gap-4 hover:border-[#D4AF37]/40 transition-colors">
        <div className="w-11 h-11 rounded-xl fv-gold-gradient flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-[#0A0A0B]" /></div>
        <div className="flex-1 text-left"><div className="font-heading text-lg text-white">Ask your AI Advisor about funding</div><div className="text-sm text-[#A1A1AA]">It knows your credit profile and can recommend the best-fit funding options.</div></div>
        <ArrowUpRight className="w-5 h-5 text-[#D4AF37] shrink-0" />
      </button>
    </div>
  );
}

export default function BusinessCredit() {
  const [wf, setWf] = useState(null);
  const [step, setStep] = useState("profile");
  const [duns, setDuns] = useState({ has_duns: null, duns_number: "", status: "" });
  const [bureaus, setBureaus] = useState({ paydex: 0, experian: null, equifax: null });
  const [cred, setCred] = useState({ bank: false, listing: false });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [editing, setEditing] = useState(false);

  const hydrate = (data) => {
    setWf(data);
    setDuns(data.duns);
    setBureaus(data.bureaus);
    const items = data.credibility.items;
    setCred({ bank: !!items.find((i) => i.id === "bank")?.done, listing: !!items.find((i) => i.id === "listing")?.done });
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/credit/workflow");
    hydrate(data);
    setStep(data.workflow.current_step || "profile");
  }, []);
  useEffect(() => { load(); }, [load]);

  const sliceFor = (s) => {
    if (s === "profile") return { credibility: cred };
    if (s === "duns") return { duns };
    if (s === "bureaus") return { bureaus };
    return {};
  };

  const persist = async (s, advance) => {
    const { data } = await api.put("/credit/workflow/step", { step: s, data: sliceFor(s), advance });
    setWf((w) => ({ ...w, ...data }));
    return data;
  };

  const goNext = async () => {
    if (step === "duns" && duns.has_duns === null) { toast.error("Please tell us whether you have a D-U-N-S number."); return; }
    setSaving(true);
    try {
      await persist(step, true);
      const idx = wf.steps.findIndex((x) => x.id === step);
      setStep(wf.steps[Math.min(idx + 1, wf.steps.length - 1)].id);
      window.scrollTo(0, 0);
    } catch { toast.error("Could not save. Please try again."); }
    finally { setSaving(false); }
  };
  const goBack = async () => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Credit autosave deferred:", e); }
    setSaving(false);
    const idx = wf.steps.findIndex((x) => x.id === step);
    setStep(wf.steps[Math.max(idx - 1, 0)].id);
    window.scrollTo(0, 0);
  };
  const jump = async (target) => {
    setSaving(true);
    try { await persist(step, false); } catch (e) { console.warn("Credit autosave deferred:", e); }
    setSaving(false);
    setStep(target); window.scrollTo(0, 0);
  };

  const refresh = async () => { const { data } = await api.get("/credit/workflow"); setWf(data); };
  const addVendor = async (payload) => {
    try { await api.post("/credit/workflow/vendors", payload); await refresh(); toast.success("Vendor account added"); }
    catch { toast.error("Could not add account"); }
  };
  const deleteVendor = async (id) => {
    try { await api.delete(`/credit/workflow/vendors/${id}`); await refresh(); toast.success("Account removed"); }
    catch { toast.error("Could not remove account"); }
  };
  const recordPayment = async (acc) => {
    const input = window.prompt(`Record a payment for ${acc.vendor_name} (current balance $${acc.balance || 0})`, acc.balance || 0);
    if (input === null) return;
    try { await api.post(`/credit/workflow/vendors/${acc.account_id}/payment`, { amount: parseFloat(input) || 0 }); await refresh(); toast.success("Payment recorded — reporting updated"); }
    catch { toast.error("Could not record payment"); }
  };

  const toggleCred = (id) => {
    setCred((c) => ({ ...c, [id]: !c[id] }));
    setWf((w) => {
      const items = w.credibility.items.map((it) => (it.id === id ? { ...it, done: !it.done } : it));
      const done = items.filter((i) => i.done).length;
      return { ...w, credibility: { ...w.credibility, items, done, score: Math.round((done / items.length) * 100) } };
    });
  };

  const generateDocs = async () => {
    setGenerating(true);
    try { const { data } = await api.post("/credit/workflow/generate-documents"); setWf((w) => ({ ...w, documents_generated: data.documents.map((d) => d.section) })); toast.success(`${data.documents.length} documents saved to your Document Library`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate documents"); }
    finally { setGenerating(false); }
  };

  const complete = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/credit/workflow/complete");
      setPlan(data.funding_plan);
      setWf((w) => ({ ...w, workflow: data.workflow, funding_readiness: data.funding_readiness, ai_review: data.ai_review }));
      setEditing(false);
      toast.success("Business credit workflow complete! 🎉");
      window.scrollTo(0, 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not complete"); }
    finally { setSaving(false); }
  };

  if (!wf) return <p className="text-[#71717A]">Loading…</p>;

  if (wf.workflow.status === "completed" && !editing) {
    return <CompletedView wf={wf} plan={plan} onEdit={() => { setEditing(true); setStep("profile"); }} />;
  }

  const completed = wf.workflow.completed_steps || [];
  const meta = wf.steps.find((s) => s.id === step);
  const idx = wf.steps.findIndex((s) => s.id === step);
  const pct = Math.round((completed.length / wf.steps.length) * 100);

  const STEP_UI = {
    profile: <ProfileStep wf={wf} credibility={cred} toggle={toggleCred} />,
    duns: <DunsStep duns={duns} setDuns={setDuns} />,
    bureaus: <BureausStep bureaus={bureaus} setBureaus={setBureaus} />,
    vendors: <VendorsStep wf={wf} onAdd={addVendor} onDelete={deleteVendor} onPayment={recordPayment} />,
    timeline: <TimelineStep wf={wf} />,
    documents: <CreditDocumentsStep hasDocs={(wf.documents_generated || []).length > 0} onGenerate={generateDocs} generating={generating} />,
    review: <CreditReviewStep review={wf.ai_review} onComplete={complete} completing={saving} />,
  };

  return (
    <div className="fv-fade-up max-w-4xl" data-testid="credit-wizard">
      <PageHeader eyebrow="Build" title="Business Credit Center"
        subtitle="A guided advisor to establish, build, monitor, and strengthen your business credit. Progress saves automatically." />

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-4 mb-6">
        <div className="flex justify-between text-xs mb-2"><span className="text-[#A1A1AA]">Overall progress</span><span className="text-[#D4AF37]" data-testid="credit-overall-pct">{pct}%</span></div>
        <Progress value={pct} className="h-2 bg-white/5" />
      </div>

      <Stepper steps={wf.steps} current={step} completed={completed} onJump={jump} />

      <div className="mb-2">
        <div className="text-[11px] uppercase tracking-wider text-[#71717A]">Step {idx + 1} of {wf.steps.length}</div>
        <h2 className="font-heading text-2xl text-white">{meta.title}</h2>
        <p className="text-sm text-[#71717A]">{meta.desc}</p>
      </div>

      <div className="mt-5 pb-24" data-testid="credit-step-content">{STEP_UI[step]}</div>

      <div className="flex items-center justify-between gap-3 pt-6 border-t border-white/8 sticky bottom-0 bg-[#0A0A0B]/80 backdrop-blur pb-2">
        <button onClick={goBack} disabled={idx === 0 || saving} data-testid="credit-back-btn"
          className="flex items-center gap-1.5 text-sm h-11 px-5 rounded-full bg-white/5 border border-white/15 text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        {step !== "review" && (
          <button onClick={goNext} disabled={saving} data-testid="credit-next-btn"
            className="flex items-center gap-1.5 fv-gold-gradient text-[#0A0A0B] rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-40">
            {saving ? "Saving…" : "Continue"} <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
